// src/hooks/usePremium.js
import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const PremiumContext = createContext(null);

export function PremiumProvider({ children }) {
  const [status,      setStatus]    = useState(null);
  const [loading,     setLoading]   = useState(true);
  const [modalOpen,   setModalOpen] = useState(false);
  const [modalFeature,setFeature]   = useState(null);

  const reduxUser      = useSelector(s => s.auth?.user);
  const reduxIsPremium = !!(reduxUser?.is_premium || reduxUser?.has_premium_access);

  const fetchStatus = useCallback(async () => {
    const token = localStorage.getItem('access_token');
    if (!token) { setLoading(false); return; }
    try {
      const { data } = await axios.get(`${API}/billing/status/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatus(data);
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // Re-fetch when Redux premium flag changes
  const prevPremiumRef = useRef(null);
  useEffect(() => {
    if (prevPremiumRef.current === null) {
      prevPremiumRef.current = reduxIsPremium;
      return;
    }
    if (prevPremiumRef.current !== reduxIsPremium) {
      prevPremiumRef.current = reduxIsPremium;
      fetchStatus();
    }
  }, [reduxIsPremium, fetchStatus]);

  // ── Derived state ──────────────────────────────────────────────────────────
  const apiIsPremium = status?.usage?.is_premium ?? false;
  const isPremium    = apiIsPremium || reduxIsPremium;

  const planType     = status?.usage?.plan_type   ?? reduxUser?.premium_plan ?? null;
  const subscription = status?.subscription       ?? null;   // full SubscriptionSerializer data
  const features     = status?.usage?.features    ?? {};

  // subscription_detail is the flat plan info added in get_usage_summary()
  // It has: plan_name, interval, amount, currency, features[], current_period_end, etc.
  const subscriptionDetail = status?.usage?.subscription_detail ?? null;

  // ── Per-feature helpers ────────────────────────────────────────────────────
  const isUnlocked = (featureKey) => {
    if (isPremium) return true;
    return features[featureKey]?.unlocked ?? false;
  };

  const dmUsed      = features.direct_messages?.used  ?? 0;
  const dmLimit     = features.direct_messages?.limit ?? 5;
  const dmRemaining = isPremium ? null : Math.max(0, dmLimit - dmUsed);

  const bidsUsed      = features.bids?.used  ?? 0;
  const bidsLimit     = features.bids?.limit ?? 5;
  const bidsRemaining = isPremium ? null : Math.max(0, bidsLimit - bidsUsed);

  const taggedSolLimit = isPremium ? null : (features.tagged_solutions?.limit ?? 5);

  // ── Modal ──────────────────────────────────────────────────────────────────
  const openUpgradeModal  = useCallback((key = null) => { setFeature(key); setModalOpen(true); }, []);
  const closeUpgradeModal = useCallback(() => { setModalOpen(false); setFeature(null); }, []);

  const guard = useCallback(async (fn, featureKey = null) => {
    try { return await fn(); }
    catch (err) {
      if (err?.response?.status === 402) { openUpgradeModal(featureKey); return null; }
      throw err;
    }
  }, [openUpgradeModal]);

  const canDM  = useCallback(() => isPremium || dmUsed  < dmLimit,   [isPremium, dmUsed,  dmLimit]);
  const canBid = useCallback(() => isPremium || bidsUsed < bidsLimit, [isPremium, bidsUsed, bidsLimit]);

  return (
    <PremiumContext.Provider value={{
      loading, isPremium, planType,
      subscription,        // full serialized Subscription object
      subscriptionDetail,  // flat plan info: plan_name, features[], current_period_end, etc.
      features,
      dmUsed, dmLimit, dmRemaining,
      bidsUsed, bidsLimit, bidsRemaining,
      taggedSolLimit,
      isUnlocked, canDM, canBid,
      modalOpen, modalFeature, openUpgradeModal, closeUpgradeModal,
      guard,
      refresh: fetchStatus,
    }}>
      {children}
    </PremiumContext.Provider>
  );
}

export function usePremium() {
  const ctx = useContext(PremiumContext);
  if (!ctx) throw new Error('usePremium must be used inside <PremiumProvider>');
  return ctx;
}